/*
 * Copyright (C) 2021 Bosch.IO GmbH. All rights reserved.
 */

import React, { useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { Table, TableHead, TableBody, Icon } from '../../atoms';
import { DataListHeader } from './dataListHead';
import { DataListBody } from './dataListBody';
import { DataListProps } from './types';
import { getId } from './utils';

const StyledDataListContainer = styled.div`
    width: 100%;
    height: 100%;
    flex: 1;
    position: relative;
`;

const StyledDataListContainerScrollable = styled(StyledDataListContainer)`
    overflow: hidden;
    overflow-y: auto;
`;

const EmptyStateStyle = styled.div`
    margin: 3rem auto;
    text-align: center;
    color: var(--plain__disabled__front__default);
`;

const StyledTable = styled(Table)`
    width: 100%;
    table-layout: fixed;
`;

function getDataListContainer(enableScrolling: boolean) {
    return enableScrolling
        ? StyledDataListContainerScrollable
        : StyledDataListContainer;
}

const SCROLL_ROW_HEIGHT = 4;
function isScrolledToBottom(container: HTMLElement) {
    return (
        container.scrollTop + container.clientHeight >=
        container.scrollHeight - 2 * SCROLL_ROW_HEIGHT
    );
}

export function DataList<TItem, TData = unknown>({
    id,
    items,
    selected,
    columns,
    groups,
    onGroupClicked,
    checked = [],
    disabled = [],
    onSelect,
    onChecked,
    onColumnHeaderClick,
    onColumnHeaderContextMenu,
    onRenderColumnHeader,
    onScrollToBottom,
    onItemChecked,
    hideCheckBoxes,
    hideHeaders,
    enableScrolling,
    emptyStateMessage,
    style,
    tableStyle,
    className,
    children,
}: DataListProps<TItem, TData>): JSX.Element {
    const DataListContainer = getDataListContainer(enableScrolling);
    const dataListContainerRef = useRef(undefined);

    useEffect(() => {
        if (enableScrolling && onScrollToBottom) {
            const dataListContainer = dataListContainerRef.current;
            const onScroll = () => {
                if (isScrolledToBottom(dataListContainer)) {
                    onScrollToBottom();
                }
            };
            dataListContainer.addEventListener('scroll', onScroll);
            return () => {
                dataListContainer.removeEventListener('scroll', onScroll);
            };
        }
    }, [dataListContainerRef, enableScrolling, onScrollToBottom]);

    const emptyState = () => (
        <EmptyStateStyle>
            <Icon iconName="list-view-mobile" />
            <p>{emptyStateMessage ?? 'Empty list'}</p>
        </EmptyStateStyle>
    );

    const tableId = useMemo(() => (id ? id : getId()), [id]);

    return (
        <DataListContainer
            className={className}
            style={style}
            ref={dataListContainerRef}
        >
            {items.length === 0 ? (
                emptyState()
            ) : (
                <StyledTable style={tableStyle}>
                    {!hideHeaders && (
                        <TableHead>
                            <DataListHeader
                                items={items}
                                hideCheckBoxes={hideCheckBoxes}
                                checked={checked}
                                columns={columns}
                                onColumnClick={onColumnHeaderClick}
                                onColumnContextMenu={onColumnHeaderContextMenu}
                                onRenderColumnHeader={onRenderColumnHeader}
                                onChecked={onChecked}
                                onItemChecked={onItemChecked}
                                tableId={tableId}
                            />
                        </TableHead>
                    )}
                    <TableBody>
                        <DataListBody
                            items={items}
                            columns={columns}
                            groups={groups}
                            onGroupClicked={onGroupClicked}
                            selected={selected}
                            checked={checked}
                            disabled={disabled}
                            hideCheckBoxes={hideCheckBoxes}
                            onSelect={onSelect}
                            onChecked={onChecked}
                            onItemChecked={onItemChecked}
                            tableId={tableId}
                        />
                    </TableBody>
                </StyledTable>
            )}
            {children}
        </DataListContainer>
    );
}
